import { trackActivityAnalyticsEvent } from "@/utils/googleAdsEvents";

const API_BASE = import.meta.env.VITE_API_URL ?? "";
const ANON_STORAGE_KEY = "fs-anon-id";
const TOKEN_KEY = "fs-token";

const getSessionToken = () => {
  if (typeof window === "undefined") return null;
  const local = localStorage.getItem(TOKEN_KEY);

  if (local) return local;

  return sessionStorage.getItem(TOKEN_KEY);
};

const buildAnonymousId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `anon-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const summarizeObject = (value: Record<string, unknown>) => {
  const keys = Object.keys(value);

  return {
    count: keys.length,
    keys: keys.slice(0, 8).join(",") || undefined,
  };
};

export const getOrCreateAnonymousId = () => {
  if (typeof window === "undefined") return "";
  const existing = localStorage.getItem(ANON_STORAGE_KEY);

  if (existing) return existing;
  const created = buildAnonymousId();

  localStorage.setItem(ANON_STORAGE_KEY, created);

  return created;
};

export function trackActivityEvent(payload: {
  eventType: string;
  path?: string;
  searchTerm?: string;
  filters?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}) {
  if (typeof window === "undefined") return;
  const eventType = String(payload.eventType || "").trim();

  if (!eventType) return;

  const anonymousId = getOrCreateAnonymousId();
  const token = getSessionToken();

  const body = {
    eventType,
    path:
      payload.path ?? `${window.location.pathname}${window.location.search}`,
    searchTerm: payload.searchTerm ?? "",
    filters: payload.filters ?? {},
    metadata: payload.metadata ?? {},
    sessionId: anonymousId,
  };
  const filtersSummary = summarizeObject(body.filters);
  const metadataSummary = summarizeObject(body.metadata);

  trackActivityAnalyticsEvent(eventType, {
    path: body.path,
    search_term: body.searchTerm || undefined,
    has_search_term: Boolean(body.searchTerm),
    filter_count: filtersSummary.count,
    filter_keys: filtersSummary.keys,
    metadata_count: metadataSummary.count,
    metadata_keys: metadataSummary.keys,
  });

  fetch(`${API_BASE}/api/activity/track`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      "x-fs-anon-id": anonymousId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  }).catch(() => {});
}
